import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { ReduxState, VerificationService, VerificationStore, ViewModel } from "../types/types";
import {
  createFetchExistingVerificationRequest,
  createFetchNewVerificationRequest,
  createResetState,
  createSubmitStep,
  createUpdateFieldValidationErrors,
  createUpdateLocale,
  createUpdateProgramTheme,
  createUpdateViewModel,
} from "../VerificationService/VerificationServiceActions";

export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;
export const useAppDispatch: () => VerificationStore["dispatch"] = useDispatch;

export const useProgramId = () => useAppSelector((state) => state.programId);
export const useProgramTheme = () => useAppSelector((state) => state.programTheme);
export const useVerificationResponse = () => useAppSelector((state) => state.verificationResponse);
export const useViewModel = <T extends ViewModel = ViewModel>() =>
  useAppSelector((state) => state.viewModel as T);

/**
 * @deprecated use only the needed state slice custom hook(s).
 */
export const useVerificationService = <
  T extends ViewModel = ViewModel,
>(): VerificationService<T> => {
  const dispatch = useAppDispatch();

  return {
    ...{
      isLoading: useAppSelector((state) => state.isLoading),
      loadingStep: useAppSelector((state) => state.loadingStep),
      isErrored: useAppSelector((state) => state.isErrored),
      fieldValidationErrors: useAppSelector((state) => state.fieldValidationErrors),
      messages: useAppSelector((state) => state.messages),
      verificationResponse: useVerificationResponse(),
      viewModel: useViewModel(),
      programTheme: useProgramTheme(),
      previousViewModel: useAppSelector((state) => state.previousViewModel as T),
      previousVerificationResponse: useAppSelector((state) => state.previousVerificationResponse),
      programId: useProgramId(),
      formValidationOptions: useAppSelector((state) => state.formValidationOptions),
      overrideStep: useAppSelector((state) => state.overrideStep),
    },
    ...{
      fetchNewVerificationRequest: createFetchNewVerificationRequest(dispatch)<T>,
      fetchExistingVerificationRequest: createFetchExistingVerificationRequest(dispatch),
      updateViewModel: createUpdateViewModel(dispatch),
      updateLocale: createUpdateLocale(dispatch, useProgramId()),
      updateProgramTheme: createUpdateProgramTheme(dispatch),
      updateFieldValidationErrors: createUpdateFieldValidationErrors(dispatch),
      submitStep: createSubmitStep(dispatch),
      resetState: createResetState(dispatch),
    },
  };
};
